import i18n from '@/i18n';
import {
  SHOW_TOAST,
  HIDE_TOAST,
  HIDE_ALL_TOASTS,
  ToastSeverity,
  ToastItem,
  ToastAnchorOrigin,
} from './toastReducer';

type ToastOptions = {
  hideDuration?: number | null;
  anchorOrigin?: ToastAnchorOrigin;
  action?: { label: string; fn: () => void };
  /** Optional second line. See `ToastItem.body`. */
  body?: string;
};

const _actionCallbacks = new Map<string, () => void>();
export const getToastActionCallback = (id: string) => _actionCallbacks.get(id) ?? null;
export const clearToastActionCallback = (id: string) => { _actionCallbacks.delete(id); };
export const clearAllToastActionCallbacks = () => { _actionCallbacks.clear(); };

const DEFAULT_ANCHOR: ToastAnchorOrigin = { vertical: 'top', horizontal: 'center' };

const makeId = (severity: ToastSeverity, message: string) => `${severity}::${message}`;

const show = (severity: ToastSeverity, message: string, options?: ToastOptions) => {
  const id = makeId(severity, message);
  if (options?.action?.fn) _actionCallbacks.set(id, options.action.fn);
  else _actionCallbacks.delete(id);
  const item: ToastItem = {
    id,
    severity,
    message,
    body: options?.body,
    hideDuration: options?.hideDuration === undefined ? 5000 : options.hideDuration,
    anchorOrigin: options?.anchorOrigin ?? DEFAULT_ANCHOR,
    actionLabel: options?.action?.label,
  };
  return { type: SHOW_TOAST, payload: item };
};

export const toast = {
  success: (message: string, options?: ToastOptions) => show('success', message, options),
  error: (message: string, options?: ToastOptions) => show('error', message, options),
  warning: (message: string, options?: ToastOptions) => show('warning', message, options),
  info: (message: string, options?: ToastOptions) => show('info', message, options),
  hide: (id?: string) => {
    if (id) return { type: HIDE_TOAST, payload: { id } };
    clearAllToastActionCallbacks();
    return { type: HIDE_ALL_TOASTS };
  },
  hideAll: () => {
    clearAllToastActionCallbacks();
    return { type: HIDE_ALL_TOASTS };
  },
};

export const getNotificationMsg = (key: string): string =>
  (i18n.getDataByLanguage(i18n.language) as any)?.['translation.notification']?.[key] ?? key;

export const toastI18n = (severity: ToastSeverity, key: string, suffix = '', options?: ToastOptions) =>
  show(severity, [getNotificationMsg(key), suffix].filter(Boolean).join(' '), options);
